import React, { useState, useMemo } from "react";
import { ArrowLeft, MapPin, Flame, Search, X, TrendingUp, Plus } from "lucide-react";
import { ExhibitionCountry, ExhibitionCity, ExhibitionState } from "@/hooks/useExhibition";
import { LiveBadge } from "@/components/live-arenas/LiveBadge";
import BackgroundBalloons from "@/components/pop-balloon/BackgroundBalloons";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import ExhibitionEmptyCountryState from "./ExhibitionEmptyCountryState";
import CustomCityEntryModal from "./CustomCityEntryModal";
import { toast } from "sonner";

interface ExhibitionCityScreenProps {
  country: ExhibitionCountry;
  cities: ExhibitionCity[];
  loading: boolean;
  remainingRequests: number;
  onBack: () => void;
  onSelectCity: (cityId: string) => void;
  selectedState?: ExhibitionState | null;
  onSelectCustomCity?: (city: { id: string; name: string; isManual: boolean }) => void;
}

const ExhibitionCityScreen: React.FC<ExhibitionCityScreenProps> = ({
  country,
  cities,
  loading,
  remainingRequests,
  onBack,
  onSelectCity,
  selectedState,
  onSelectCustomCity,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showCityModal, setShowCityModal] = useState(false);

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return cities;
    return cities.filter(city => city.name.toLowerCase().includes(query));
  }, [cities, searchQuery]);

  // Get trending cities (sorted by activity)
  const trendingCities = useMemo(() => {
    return [...filteredCities]
      .filter(city => city.participantCount > 0 || city.boothCount > 0)
      .sort((a, b) => (b.participantCount * 2 + b.boothCount) - (a.participantCount * 2 + a.boothCount))
      .slice(0, 4);
  }, [filteredCities]);

  // Get remaining cities (excluding trending when not searching)
  const remainingCities = useMemo(() => {
    if (searchQuery) return filteredCities;
    const trendingIds = new Set(trendingCities.map(c => c.id));
    return filteredCities.filter(city => !trendingIds.has(city.id));
  }, [filteredCities, trendingCities, searchQuery]);

  return (
    <>
      <div className="min-h-screen sky-gradient relative overflow-hidden">
      <BackgroundBalloons />

      <div className="relative z-10 flex flex-col min-h-screen pb-20">
        {/* Header */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <button onClick={onBack} className="p-2 -ml-2">
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
            <LiveBadge variant="glow" />
          </div>

          <div className="text-center mt-2">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-2xl">{country.flag}</span>
              <h1 className="text-3xl font-bold text-foreground">
                {selectedState ? selectedState.name : country.name}
              </h1>
            </div>
            <p className="text-muted-foreground text-sm">
              {selectedState ? `${country.name} · Love Exhibition` : "Love Exhibition"}
            </p>
          </div>
        </div>

        {/* Country stats */}
        <div className="mx-4 mt-4 p-4 rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 shadow-md">
          <div className="flex items-center justify-around">
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{cities.length}</p>
              <p className="text-xs text-muted-foreground">Cities</p>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center">
              <p className="text-2xl font-bold text-pop">{country.boothCount}</p>
              <p className="text-xs text-muted-foreground">Live Booths</p>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center">
              <p className="text-lg font-bold text-muted-foreground">{remainingRequests}/5</p>
              <p className="text-xs text-muted-foreground">Requests Left</p>
            </div>
          </div>
        </div>

        {/* Search Input */}
        {cities.length > 3 && (
          <div className="mx-4 mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search cities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 h-11 rounded-xl bg-white/60 backdrop-blur-sm border-white/50 focus-visible:ring-1 focus-visible:ring-primary"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Cities grid */}
        <div className="flex-1 overflow-y-auto px-4 mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 rounded-full border-4 border-pop border-t-transparent animate-spin" />
            </div>
          ) : filteredCities.length === 0 && searchQuery ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No cities found matching "{searchQuery}"</p>
              <button 
                onClick={() => setSearchQuery("")}
                className="mt-2 text-sm text-primary hover:underline"
              >
                Clear search
              </button>
              {onSelectCustomCity && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 gap-2"
                  onClick={() => setShowCityModal(true)}
                >
                  <Plus className="w-4 h-4" />
                  Use "{searchQuery}" as custom city
                </Button>
              )}
            </div>
          ) : cities.length === 0 && country.boothCount === 0 ? (
            <ExhibitionEmptyCountryState 
              country={country}
              onEnterCustomCity={() => {
                setShowCityModal(true);
              }}
            />
          ) : (
            <div className="space-y-4 pb-8">
              {/* Add Custom City Button */}
              {onSelectCustomCity && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2"
                  onClick={() => setShowCityModal(true)}
                >
                  <Plus className="w-4 h-4" />
                  Enter a different city
                </Button>
              )}

              {/* Trending Cities */}
              {trendingCities.length > 0 && !searchQuery && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-bold">Trending</span>
                      <Flame className="w-3 h-3 animate-pulse" />
                    </div>
                    <span className="text-xs text-muted-foreground">Most active</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {trendingCities.map((city, index) => (
                      <CityCard 
                        key={`trending-${city.id}`} 
                        city={city} 
                        onClick={() => onSelectCity(city.id)}
                        showRank={index + 1}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* All/Remaining Cities */}
              {remainingCities.length > 0 && (
                <div className="space-y-3">
                  {trendingCities.length > 0 && !searchQuery && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">All Cities</span>
                      <span className="text-xs text-muted-foreground">({remainingCities.length})</span>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    {remainingCities.map((city) => (
                      <CityCard key={city.id} city={city} onClick={() => onSelectCity(city.id)} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>

      {/* Custom City Entry Modal */}
      {onSelectCustomCity && (
        <CustomCityEntryModal
          isOpen={showCityModal}
          onClose={() => setShowCityModal(false)}
          country={country}
          onSelectCity={(city) => {
            onSelectCustomCity(city);
            setShowCityModal(false);
          }}
        />
      )}
    </>
  );
};

interface CityCardProps {
  city: ExhibitionCity;
  onClick: () => void;
  showRank?: number;
}

const CityCard: React.FC<CityCardProps> = ({ city, onClick, showRank }) => {
  const isActive = city.participantCount > 0;
  const isHot = city.hotBooths > 0;

  return (
    <button
      onClick={onClick}
      className={cn(
        "relative p-4 rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 shadow-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-xl text-left overflow-hidden",
        isActive && "ring-2 ring-pop/30",
        isHot && "ring-2 ring-amber-400/50"
      )}
    >
      {/* Rank badge */}
      {showRank && (
        <div className={cn(
          "absolute top-2 left-2 w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold shadow-lg",
          showRank === 1 ? "bg-amber-500 text-white" :
          showRank === 2 ? "bg-gray-400 text-white" :
          showRank === 3 ? "bg-amber-700 text-white" :
          "bg-white/90 text-foreground"
        )}>
          {showRank}
        </div>
      )}

      {/* Hot badge */}
      {isHot && !showRank && (
        <div className="absolute top-2 right-2">
          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-medium">
            <Flame className="w-2.5 h-2.5" />
            HOT
          </span>
        </div>
      )}

      {/* Preview faces */}
      {city.previewFaces.length > 0 && (
        <div className={cn("flex -space-x-2 mb-3", showRank && "ml-6")}>
          {city.previewFaces.slice(0, 3).map((face, i) => (
            <img
              key={i}
              src={face}
              alt=""
              className="w-8 h-8 rounded-full border-2 border-white object-cover"
            />
          ))}
          {city.participantCount > 3 && (
            <div className="w-8 h-8 rounded-full bg-muted border-2 border-white flex items-center justify-center text-xs font-medium text-muted-foreground">
              +{city.participantCount - 3}
            </div>
          )}
        </div>
      )}

      <h3 className="font-bold text-foreground truncate">{city.name}</h3>

      <div className="flex items-center gap-2 mt-1 text-sm">
        <span className="text-muted-foreground">{city.boothCount} booths</span>
        {isActive && (
          <span className="flex items-center gap-1 text-green-600 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            {city.participantCount}
          </span>
        )}
      </div>
    </button>
  );
};

export default ExhibitionCityScreen;
